import { Button, ButtonTypes, Icon, Input } from "@clintonelec/react-storybook";
import type { UpdateAccountPayload, UserResource } from "Interfaces";
import { memo, useState } from "react";
import { produce } from "immer";
import "Components/Dashboards/SupportDashboard/UserInformation/UserInformation.less";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectUserHostnames, updateUserAction } from "Data/Redux/Slices/User";
import { NotificationType, notify } from "src/Notifications";
import type { SerializedError } from "@reduxjs/toolkit";
import { formatDate } from "@clintonelec/typescriptutils";
import DeleteAccountModal from "./DeleteAccountModal";
import HostnameTable from "Components/HostnameTable";

type UserInformationProps = {
	selectedUser: UserResource;
	onBackClick: () => void;
};

type UserEditState = {
	editing: boolean;
	value: string;
};

function UserInformation({ selectedUser, onBackClick: handleGoBack }: UserInformationProps) {
	const dispatch = useAppDispatch();
	const hostnames = useAppSelector(state => selectUserHostnames(state, selectedUser.id));
	const editUser = (payload: UpdateAccountPayload) => dispatch(
		updateUserAction({ id: selectedUser.id, ...payload })
	);

	const [ submitted, setSubmitted ] = useState(false);
	const [ userErrors, setUserErrors ] = useState<Record<string, string>>({});
	const [ fieldEdits, setFieldEdits ] = useState<Record<keyof UpdateAccountPayload, UserEditState>>({
		username: {
			editing: false,
			value: selectedUser.username
		},
		email: {
			editing: false,
			value: selectedUser.email
		},
		name: {
			editing: false,
			value: selectedUser.name
		}
	});

	const handleUserFieldUpdate = (field: keyof UpdateAccountPayload) => (value: string) => {
		setFieldEdits(produce(fieldEdits, draft => {
			draft[ field ].value = value;
		}));
	};

	const handleSuffixClick = (field: keyof UpdateAccountPayload) => () => {
		const isEditing = fieldEdits[ field ].editing;

		if (isEditing) {
			updateUser();
		}

		setFieldEdits(produce(fieldEdits, draft => {
			draft[ field ].editing = !isEditing;
		}));
	};

	const updateUser = () => {
		const payload: UpdateAccountPayload = {
			username: fieldEdits.username.value,
			email: fieldEdits.email.value,
			name: fieldEdits.name.value
		};

		setSubmitted(true);

		editUser(payload)
			.unwrap()
			.then(() => {
				setSubmitted(false);
				notify(NotificationType.SUCCESS, "User updated successfully.");
				setUserErrors({});
			})
			.catch((error: SerializedError) => {
				setUserErrors(JSON.parse(error.message ?? ""));
				notify(NotificationType.ERROR, "There was an error updating the user.");
			});
	};

	const getFieldProps = (field: keyof UpdateAccountPayload) => {
		const iconName = fieldEdits[ field ].editing ? "save" : "pencil";
		const icon = (
			<Icon
				name={ iconName }
				onClick={ handleSuffixClick(field) }
				className="edit-icon"
			/>
		);

		return {
			disabled: !fieldEdits[ field ].editing,
			value: fieldEdits[ field ].value,
			suffix: icon,
			onUpdate: handleUserFieldUpdate(field),
			noValidate: !submitted,
			validator: () => !userErrors[ field ],
			validityMessage: userErrors[ field ]
		};
	};

	return (
		<div className="user-information">
			<div className="go-back" onClick={ handleGoBack }>
				<Icon name="arrow-left" />
				Go Back
			</div>
			<div className="information-container">
				<div className="title-container">
					<h4>Edit User: { selectedUser.name }</h4>
				</div>
				<div className="content-container">
					<div className="user-content-container">
						<div className="account-section">
							<label>Username</label>
							<Input { ...getFieldProps("username") } />
							<label>Email*</label>
							<Input { ...getFieldProps("email") } required />
							<label>Name*</label>
							<Input { ...getFieldProps("name") } required />
						</div>
						<div className="account-section">
							<label>Role</label>
							<Input defaultValue={ selectedUser.role } disabled />
							<label>Account Created</label>
							<Input defaultValue={ formatDate(new Date(), "MMM DD, YYYY - hh:mm:ss") } disabled />
							<label>Status</label>
							<Input defaultValue={ selectedUser.verified ? "Verified" : "Un-Verified" } disabled />
						</div>
						<div className="account-actions-section">
							<Button fill type={ ButtonTypes.TERTIARY } htmlType="button">
								Disable Account
							</Button>
							<Button fill type={ ButtonTypes.TERTIARY } htmlType="button">
								Reset Password
							</Button>
							<DeleteAccountModal selectedUser={ selectedUser }>
								<Button fill type={ ButtonTypes.DANGER } htmlType="button">
									Delete Account
								</Button>
							</DeleteAccountModal>
						</div>
					</div>
					<HostnameTable hostnames={ hostnames } selectedUser={ selectedUser } />
				</div>
			</div>
		</div>
	);
}

export default memo(UserInformation);
